import React, { useState, useEffect, useRef } from "react";
import './Store.css';
import { useNavigate, useLocation, Outlet, useParams } from "react-router-dom";
import Header from "./Header";
import p from './images/profile.jpg';

const SLOW_MS = 2000;
function _checkSlow(label, t0) {
    const ms = Date.now() - t0;
    if (ms > SLOW_MS)
        console.error(`[SLOW] ${label} took ${ms}ms (>${SLOW_MS}ms).`);
    else
        console.debug(`[TIMING] ${label} — ${ms}ms`);
}

// ── Module-level cache (survives tab switches, resets on full page reload) ────
const _cache = new Map();
const CACHE_TTL = 5 * 60 * 1000;
const cacheGet = (k) => { const e = _cache.get(k); return (e && Date.now() < e.exp) ? e.v : null; };
const cacheSet = (k, v) => _cache.set(k, { v, exp: Date.now() + CACHE_TTL });

const storageGet = (key) => {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    try {
        const raw = window.localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const storageSet = (key, value) => {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore storage failures
    }
};

// ── Preload image into browser cache before React renders the <img> ───────────
// This tells the browser to start fetching the image over the network NOW,
// eliminating the gap between "API responded with URL" and "image downloaded".
function preloadImage(url) {
    if (!url || url === p) return;
    try {
        if (document.querySelector(`link[rel="preload"][href="${url}"]`)) return;
        const link = document.createElement('link');
        link.rel  = 'preload';
        link.as   = 'image';
        link.href = url;
        // Do NOT set crossOrigin — S3 images lack CORS headers and a CORS
        // preload would be blocked, then throw, poisoning the promise chain.
        // The <img> tag renders fine without a CORS preload.
        document.head.appendChild(link);
    } catch {}
}

// ── Eager prefetch — fires when the JS module loads, before React mounts ──────
const _EAGER_API = process.env.REACT_APP_LIST_PAGE_API_URL
    || process.env.REACT_APP_API_URL
    || 'https://pyate-backend-production-f7ce.up.railway.app';

const _headerInflight = new Map();
const _aboutInflight  = new Map();
const _postsInflight  = new Map();

if (typeof fetch !== 'undefined') { fetch(`${_EAGER_API}/ping`).catch(() => {}); }

// Posts.js storageGet expects { val, exp } format — use this wrapper so
// the eager prefetch result is readable by Posts even after a page reload.
const _storageSetTTL = (key, value, ttl = CACHE_TTL) => {
    try {
        window.localStorage.setItem(key, JSON.stringify({ val: value, exp: Date.now() + ttl }));
    } catch {}
};

function _eagerPrefetch(pageid) {
    if (!pageid || typeof fetch === 'undefined') return;
    if (!_aboutInflight.has(pageid) && !cacheGet(`about:${pageid}`)) {
        const p$ = fetch(`${_EAGER_API}/get_about_page_for_store?pageid=${pageid}`)
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(d => { cacheSet(`about:${pageid}`, d); _aboutInflight.delete(pageid); return d; })
            .catch(() => { _aboutInflight.delete(pageid); });
        _aboutInflight.set(pageid, p$);
    }
    if (!_postsInflight.has(pageid) && !cacheGet(`page_posts:${pageid}`)) {
        const p$ = fetch(`${_EAGER_API}/page/${pageid}/posts`)
            .then(r => r.ok ? r.json() : Promise.reject(r.status))
            .then(d => {
                cacheSet(`page_posts:${pageid}`, d);
                // Also persist to localStorage with TTL wrapper so Posts.js
                // can read it instantly even if the component mounts before
                // the Outlet context propagates.
                _storageSetTTL(`page_posts:${pageid}`, d);
                _postsInflight.delete(pageid);
                return d;
            })
            .catch(() => { _postsInflight.delete(pageid); });
        _postsInflight.set(pageid, p$);
    }
}
;(function () { const m = window.location.pathname.match(/\/store\/(\d+)/); if (m) _eagerPrefetch(m[1]); })();

function Store() {
    
    const API_URL  = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';
    const LIST_API_URL = process.env.REACT_APP_LIST_PAGE_API_URL || API_URL;

    const navigate = useNavigate();
    const location = useLocation();
    const { pageid } = useParams();

    // ── Pre-fill from navigation state (zero-lag first paint) ─────────────────
    // Priority order:
    //   1. location.state — passed by the list page when navigating here (fastest)
    //   2. store_header:{pageid} in localStorage — survives reloads, new tabs, sessions
    //   3. EditStore localStorage keys — only set if user came from their own edit view
    //   4. Module-level _cache — survives tab switches within the same session
    //   5. Empty strings / default avatar (triggers skeleton)
    const navState     = location.state || {};
    // Read persisted header from localStorage first — this is the key fast path.
    // storageGet now uses localStorage (was sessionStorage, which clears on tab close).
    const storedHeader = pageid ? storageGet(`store_header:${pageid}`) : null;
    // Module-level cache also checked (faster than localStorage parse, but session-only)
    const cachedHeader = pageid ? cacheGet(`header:${pageid}`) : null;
    // EditStore-specific keys (only present when user owns the page)
    const cachedImagePageId = (typeof window !== 'undefined' && pageid)
        ? window.localStorage.getItem('cached_image_page_id') : null;
    const isSameCachedPage  = pageid && cachedImagePageId && String(pageid) === String(cachedImagePageId);
    const editStoreProfile  = isSameCachedPage ? window.localStorage.getItem('cached_profile_image') : null;
    const editStoreBanner   = isSameCachedPage ? window.localStorage.getItem('cached_banner_image')  : null;
    const editStorePageName = isSameCachedPage ? window.localStorage.getItem('pagename')             : null;
    const editStoreDesc     = isSameCachedPage ? window.localStorage.getItem('description')          : null;

    // Resolve initial values — first non-empty value wins
    const initialTitle        = navState.title || navState.page_name || navState.pagename
        || cachedHeader?.pagename || storedHeader?.pagename || editStorePageName || "";
    const initialProfileImage = navState.profile_image_url
        || cachedHeader?.profile_image_url || storedHeader?.profile_image_url || editStoreProfile || p;
    const initialBannerImage  = navState.banner_image_url
        || cachedHeader?.banner_image_url  || storedHeader?.banner_image_url  || editStoreBanner  || '';
    const initialDescription  = navState.description
        || cachedHeader?.description || storedHeader?.description || editStoreDesc || "";

    // Preload images from nav state or persisted cache BEFORE first render
    const preloadedRef = useRef(false);
    if (!preloadedRef.current) {
        preloadedRef.current = true;
        preloadImage(initialProfileImage);
        preloadImage(initialBannerImage);
    }

    const [pageTitle,       setPageTitle]    = useState(initialTitle);
    const [pageDescription, setPageDesc]     = useState(initialDescription);
    const [profileImage,    setProfileImage] = useState(initialProfileImage);
    const [bannerImage,     setBannerImage]  = useState(initialBannerImage);
    const [isFollowing,     setIsFollowing]  = useState(false);
    const [followText,      setFollowText]   = useState("+ Follow");
    // Derive active tab from the current URL so a direct /store/:id/Posts link
    // highlights "Posts" immediately without waiting for navigation state.
    const [activeTab,       setActiveTab]    = useState(() => {
        const path = location.pathname.toLowerCase();
        if (path.endsWith('/posts')) return 'Posts';
        return 'About';
    });
    // Seed userId from localStorage immediately — eliminates get-user-id round-trip
    // on repeat visits (same session or cached). Updated by the fetch below if stale.
    const [userId,          setUserId]       = useState(() => {
        try { return localStorage.getItem('user_id') || null; } catch { return null; }
    });
    const [headerReady,     setHeaderReady]  = useState(!!initialTitle);

    // About and Posts data — seeded from module cache if eager prefetch already resolved
    const [aboutData,  setAboutData]  = useState(() => cacheGet(`about:${pageid}`) || null);
    const [postsData,  setPostsData]  = useState(() => cacheGet(`page_posts:${pageid}`) || null);

    // Resolve about + posts from inflight promises (may already be resolved)
    useEffect(() => {
        if (!pageid) return;
        const ap = _aboutInflight.get(pageid);
        if (ap) ap.then(d => { if (d) setAboutData(d); }).catch(() => {});
        else if (!aboutData) {
            const cached = cacheGet(`about:${pageid}`);
            if (cached) { setAboutData(cached); return; }
            fetch(`${LIST_API_URL}/get_about_page_for_store?pageid=${pageid}`)
                .then(r => r.ok ? r.json() : null)
                .then(d => { if (d) { cacheSet(`about:${pageid}`, d); setAboutData(d); } })
                .catch(() => {});
        }
        const pp = _postsInflight.get(pageid);
        if (pp) pp.then(d => { if (d) setPostsData(d); }).catch(() => {});
        else if (!postsData) {
            const cached = cacheGet(`page_posts:${pageid}`);
            if (cached) { setPostsData(cached); return; }
            fetch(`${LIST_API_URL}/page/${pageid}/posts`)
                .then(r => r.ok ? r.json() : null)
                .then(d => { if (d) { cacheSet(`page_posts:${pageid}`, d); setPostsData(d); } })
                .catch(() => {});
        }
    }, [pageid]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Single effect — all 3 fetches fire simultaneously ─────────────────────
    useEffect(() => {
        if (!pageid) return;

        const t0    = Date.now();
        const token = localStorage.getItem("token");
        const hKey  = `header:${pageid}`;

        // ── A: header ────────────────────────────────────────────────────────
        // Check module cache first (fastest), then fire network fetch.
        // The network fetch may already be in-flight if _eagerHeaderFetch was
        // called at module-load time (see below the component definition).
        // Storing the promise in _cache under the hKey means concurrent callers
        // (StrictMode double-invoke, tab-switch remount) share the same request.
        const existingCacheHit = cacheGet(hKey);
        const headerPromise = existingCacheHit
            ? Promise.resolve(existingCacheHit)
            : (_headerInflight.get(pageid) || (() => {
                const p$ = fetch(`${LIST_API_URL}/api/get_store_header?pageid=${pageid}`)
                    .then(r => r.ok ? r.json() : Promise.reject(r.status))
                    .then(data => {
                        const profileUrl = data.profile_image_url || '';
                        const bannerUrl  = data.banner_image_url  || '';
                        // Fire-and-forget — CORS failures on preload must NOT
                        // reject this promise or the fallback path triggers.
                        try { preloadImage(profileUrl); } catch {}
                        try { preloadImage(bannerUrl);  } catch {}
                        const entry = {
                            pagename:          data.pagename    || "",
                            description:       data.description || "",
                            profile_image_url: profileUrl,
                            banner_image_url:  bannerUrl,
                        };
                        cacheSet(hKey, entry);
                        storageSet(`store_header:${pageid}`, entry);
                        _headerInflight.delete(pageid);
                        return entry;
                    })
                    .catch(err => { _headerInflight.delete(pageid); return Promise.reject(err); });
                _headerInflight.set(pageid, p$);
                return p$;
            })());

        // ── B: userId — localStorage hit skips network entirely ────────────
        // Priority: module cache (fastest) → localStorage (survives reload) → network.
        // This means /api/get-user-id is only called once per browser session.
        const cachedUid = cacheGet('userId');
        const lsUid     = !cachedUid ? (() => {
            try { const v = localStorage.getItem('user_id'); return v || null; } catch { return null; }
        })() : null;
        if (lsUid && !cachedUid) cacheSet('userId', lsUid); // warm module cache from LS
        const userPromise = (cachedUid || lsUid)
            ? Promise.resolve(cachedUid || lsUid)
            : fetch(`${API_URL}/api/get-user-id`, {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    const uid = d?.user_id ?? null;
                    if (uid) {
                        cacheSet('userId', uid);
                        try { localStorage.setItem('user_id', String(uid)); } catch {}
                    }
                    return uid;
                })
                .catch(() => null);

        // ── C: follow status — short-lived module cache (30s) ───────────────
        // Caching eliminates: (a) React StrictMode double-invoke firing 2 requests,
        // (b) repeat navigations to the same store page re-fetching on every mount.
        // 30s is short enough that follow state stays current for normal use.
        const followCacheKey = `follow:${pageid}`;
        const cachedFollow   = cacheGet(followCacheKey);
        const followPromise  = cachedFollow
            ? Promise.resolve(cachedFollow)
            : fetch(`${LIST_API_URL}/api/follow-status?page_id=${pageid}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then(r => r.ok ? r.json() : null)
                .then(d => {
                    if (d) _cache.set(followCacheKey, { v: d, exp: Date.now() + 30_000 });
                    return d;
                })
                .catch(() => null);

        // ── Apply header the moment it resolves — don't wait for user/follow ──
        headerPromise
            .then(data => {
                if (!data) return;
                setPageTitle(data.pagename || "");
                setPageDesc(data.description || "");
                setProfileImage(data.profile_image_url || p);
                setBannerImage(data.banner_image_url   || '');
                setHeaderReady(true);
                _checkSlow(`get_store_header resolved`, t0);
            })
            .catch(err => {
                // Fallback: 3 individual endpoints in parallel if get_store_header fails
                console.warn(`[Store] get_store_header failed (${err}), falling back`);
                const getProfileImg  = fetch(`${LIST_API_URL}/api/get_page_profile_image?pageid=${pageid}`)
                    .then(r => r.ok ? r.json() : null).catch(() => null);
                const getBanner      = fetch(`${LIST_API_URL}/api/get_banner_image_public?pageid=${pageid}`)
                    .then(r => r.ok ? r.json() : null).catch(() => null);
                const getPageDetails = fetch(`${LIST_API_URL}/page/get_page_details_store?pageid=${pageid}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).then(r => r.ok ? r.json() : null).catch(() => null);

                Promise.all([getProfileImg, getBanner, getPageDetails]).then(([imgData, bannerData, pageDetails]) => {
                    const profileUrl = imgData?.profile_image_url || '';
                    const bannerUrl  = bannerData?.banner_image_url || '';
                    const name       = pageDetails?.pagename || pageDetails?.page_name || "";
                    const desc       = pageDetails?.description || "";
                    try { preloadImage(profileUrl); } catch {}
                    try { preloadImage(bannerUrl);  } catch {}
                    setPageTitle(name);
                    setPageDesc(desc);
                    setProfileImage(profileUrl || p);
                    setBannerImage(bannerUrl);
                    setHeaderReady(true);
                    const fallbackEntry = { pagename: name, description: desc,
                        profile_image_url: profileUrl, banner_image_url: bannerUrl };
                    cacheSet(hKey, fallbackEntry);
                    storageSet(`store_header:${pageid}`, fallbackEntry);
                    _checkSlow(`fallback header resolved`, t0);
                });
            });

        // ── userId + follow resolve independently — don't block header UI ─────
        Promise.all([userPromise, followPromise])
            .then(([uid, follow]) => {
                if (uid)    setUserId(uid);
                if (follow) {
                    setIsFollowing(follow.following);
                    setFollowText(follow.following ? "✓ Following" : "+ Follow");
                }
                _checkSlow(`user+follow resolved`, t0);
            })
            .catch(err => console.error("[Store] auth error:", err));

    }, [pageid, API_URL, LIST_API_URL]);

    const handleFollowClick = async () => {
        if (!userId || !pageid) return;
        const t0 = Date.now();
        try {
            const res  = await fetch(`${LIST_API_URL}/api/follow`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
                body: JSON.stringify({ user_id: userId, page_id: pageid }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to update follow status");
            setIsFollowing(data.following);
            setFollowText(data.following ? "✓ Following" : "+ Follow");
            _checkSlow(`POST /api/follow`, t0);
        } catch (err) {
            console.error("[Store] follow error:", err.message);
        }
    };

    const tabs = [
        { name: "About", path: "about" },
        { name: "Posts", path: "Posts" },
    ];

    const handleTabClick = (tab) => {
        setActiveTab(tab.name);
        navigate(`/store/${pageid}/${tab.path}`);
    };

    return (
        <>
            {/* Preconnect to S3 so TCP/TLS handshake starts before image URL is known */}
            <link rel="preconnect" href="https://pyate-images.s3.ap-southeast-1.amazonaws.com" crossOrigin="anonymous" />
            <Header />
            <div className="store-page-wrapper">
                <div className="store-profile-card">

                    {/* ── Banner ── */}
                    <div
                        className="store-banner"
                        style={bannerImage ? {
                            backgroundImage:    `url(${bannerImage})`,
                            backgroundSize:     "cover",
                            backgroundPosition: "center",
                            backgroundRepeat:   "no-repeat",
                        } : {}}
                    >
                        {!bannerImage && <div className="store-banner-gradient" />}
                    </div>

                    {/* ── Profile body ── */}
                    <div className="store-profile-body">
                        <div className="store-avatar-row">
                            <div className="store-avatar-wrap">
                                {/*
                                 * Always render <img> — src starts as placeholder and
                                 * updates to real URL once fetch resolves.
                                 * Because we preloaded the real URL, the browser already
                                 * has it cached by the time src changes — instant swap.
                                 */}
                                <img
                                    src={profileImage}
                                    alt={pageTitle || "Store"}
                                    className="store-avatar"
                                    loading="eager"
                                    fetchPriority="high"
                                    decoding="async"
                                    onError={e => { e.target.src = p; }}
                                />
                            </div>
                        </div>

                        <div className="store-name-row">
                            <div className="store-name-block">
                                {headerReady
                                    ? <h1 className="store-name">{pageTitle || "Store"}</h1>
                                    : (
                                        /*
                                         * Skeleton shown only if no nav state title was passed.
                                         * Add to Store.css:
                                         *
                                         * .store-skeleton-line {
                                         *   border-radius: 4px;
                                         *   background: linear-gradient(90deg,
                                         *     var(--color-bg-secondary, #e0e0e0) 25%,
                                         *     var(--color-bg-tertiary,  #f0f0f0) 50%,
                                         *     var(--color-bg-secondary, #e0e0e0) 75%);
                                         *   background-size: 200% 100%;
                                         *   animation: shimmer 1.4s infinite;
                                         * }
                                         * .store-skeleton-line.wide   { width: 180px; height: 22px; }
                                         * .store-skeleton-line.narrow { width: 110px; height: 16px; margin-top: 8px; }
                                         * @keyframes shimmer {
                                         *   0%   { background-position: 200% 0; }
                                         *   100% { background-position: -200% 0; }
                                         * }
                                         */
                                        <>
                                            <div className="store-skeleton-line wide" />
                                            <div className="store-skeleton-line narrow" />
                                        </>
                                    )
                                }
                                {headerReady && pageDescription &&
                                    <p className="store-desc">{pageDescription}</p>
                                }
                            </div>
                            <button
                                className={`store-follow-btn ${isFollowing ? "following" : ""}`}
                                onClick={handleFollowClick}
                            >
                                {followText}
                            </button>
                        </div>
                    </div>

                    {/* ── Navigation tabs ── */}
                    <div className="store-tabs">
                        {tabs.map(tab => (
                            <button
                                key={tab.name}
                                className={`store-tab-btn ${activeTab === tab.name ? "active" : ""}`}
                                onClick={() => handleTabClick(tab)}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </div>

                    {/* ── Tab content ── */}
                    <div className="store-tab-content">
                        <Outlet context={{ pageId: pageid, userId, aboutData, postsData, profileImage }} />
                    </div>

                </div>
            </div>
        </>
    );
}

export default Store;